import type {
  ScenarioKey,
  Simulation,
  SimulationCreate,
  SimulationParamsUpdate,
  SimulationTimeseries,
  SimulationTimeseriesResolution,
  SimulationUpdateResult,
  DayTypeDistribution,
  DayProfileRead,
  ConsumptionFingerprintRead,
} from "@/types";
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from "./client";

/**
 * Endpoints `/api/v1/simulations` — création, run, lecture détail + timeseries.
 *
 * Cf. `backend/app/simulations/router.py`. Toutes les routes sont scopées par
 * le cookie `sunmatch_anon` (S2-8).
 */

const BASE = "/api/v1/simulations";

/**
 * Liste les simulations de la session courante (S3-4, ordre antéchronologique).
 *
 * Le backend renvoie un :class:`SimulationRead` complet (avec ``kpi`` si la
 * simulation est ``completed``), pas un résumé. On expose donc directement
 * `Simulation[]` pour rendre les KPIs sur le dashboard sans appel additionnel.
 */
export function listSimulations(limit?: number): Promise<Simulation[]> {
  const config = limit !== undefined ? { params: { limit } } : undefined;
  return apiGet<Simulation[]>(BASE, config);
}

/** Crée une simulation au statut `pending`. */
export function createSimulation(
  payload: SimulationCreate,
): Promise<Simulation> {
  return apiPost<Simulation, SimulationCreate>(BASE, payload);
}

/**
 * Déclenche le calcul (BackgroundTask côté serveur). Le backend renvoie 202
 * avec la simulation au statut `running` ; le client doit ensuite poller
 * `getSimulation` pour observer la transition vers `completed` / `failed`.
 */
export function runSimulation(id: string): Promise<Simulation> {
  return apiPost<Simulation>(`${BASE}/${id}/run`);
}

export function getSimulation(id: string): Promise<Simulation> {
  return apiGet<Simulation>(`${BASE}/${id}`);
}

/**
 * Supprime définitivement une simulation (`DELETE /api/v1/simulations/{id}`,
 * S23-2). Réponse **204 sans corps** (hard-delete, cascade FK sur les
 * `ProductionTimeseries` — la conso du site est intacte). Owner-scopé :
 * `404 SIMULATION_NOT_FOUND` (inexistante / cross-owner, anti-énumération).
 */
export function deleteSimulation(id: string): Promise<void> {
  return apiDelete<void>(`${BASE}/${id}`);
}

/**
 * Édite **in-place** les paramètres de dimensionnement et **relance** le calcul
 * (`PUT /api/v1/simulations/{id}`, S23-4). Les anciens KPI sont **écrasés** ; la
 * simu repasse `running` (re-run async). Renvoie l'enveloppe `{ simulation,
 * relaunched }`.
 *
 * ⚠️ À NE PAS confondre avec le flux « variante » `?fromSimulation` (POST =
 * nouvelle simu) : ici on modifie la simu existante.
 *
 * Codes d'erreur propagés en `ApiError` : `404 SIMULATION_NOT_FOUND`
 * (inexistante / cross-owner), `409 SIMULATION_RUN_IN_PROGRESS` (la simu est
 * déjà `running`), `422 SIMULATION_PARAMS_INVALID` (bornes / période invalides).
 */
export function updateSimulationParams(
  id: string,
  params: SimulationParamsUpdate,
): Promise<SimulationUpdateResult> {
  return apiPut<SimulationUpdateResult, SimulationParamsUpdate>(
    `${BASE}/${id}`,
    params,
  );
}

/**
 * Met à jour l'**ajustement de consommation dégradé persistant** (S27, ADR-011).
 * `PATCH /api/v1/simulations/{id}`, body `{ consumption_adjustment }` →
 * `SimulationRead` à jour, **sans re-run** (métadonnée seule).
 *
 * Codes d'erreur propagés en `ApiError` : `404 SIMULATION_NOT_FOUND`
 * (inexistante / cross-owner), `409 SIMULATION_NOT_DEGRADED` (la simu n'est pas
 * en mode dégradé — ne survient jamais en usage normal, le toggle n'étant rendu
 * qu'en dégradé), `422` (valeur hors `n1`|`n12`|`n2`).
 */
export function patchConsumptionAdjustment(
  id: string,
  consumptionAdjustment: ScenarioKey,
): Promise<Simulation> {
  return apiPatch<Simulation, { consumption_adjustment: ScenarioKey }>(
    `${BASE}/${id}`,
    { consumption_adjustment: consumptionAdjustment },
  );
}

/**
 * Récupère la série temporelle conso/prod d'une simulation (S3-8a backend).
 *
 * Le backend accepte une résolution ``30min``, ``1h``, ``1d`` ou ``1month``
 * et renvoie une 400 ``RESOLUTION_NOT_SUPPORTED`` pour toute autre valeur.
 * Les bornes ``from``/``to`` sont optionnelles ; à défaut, le backend renvoie
 * l'ensemble de la période simulée.
 *
 * Note d'API : le backend attend le query param ``frm`` (et non ``from``)
 * pour éviter la collision avec le mot-clé Python ``from``.
 */
export function getSimulationTimeseries(
  id: string,
  params?: {
    from?: string;
    to?: string;
    resolution?: SimulationTimeseriesResolution;
    /** S25 étape 2 — scénario dégradé (`central` | `n1` | `n2`). */
    scenario?: string;
  },
): Promise<SimulationTimeseries> {
  const queryParams: Record<string, string> = {};
  if (params?.from) {
    queryParams.frm = params.from;
  }
  if (params?.to) {
    queryParams.to = params.to;
  }
  if (params?.resolution) {
    queryParams.resolution = params.resolution;
  }
  // `central` = défaut backend → on n'envoie le param que pour n1/n2 (garde les
  // requêtes du chemin classique strictement identiques).
  if (params?.scenario && params.scenario !== "central") {
    queryParams.scenario = params.scenario;
  }
  return apiGet<SimulationTimeseries>(`${BASE}/${id}/timeseries`, {
    params: queryParams,
  });
}

/**
 * Récupère la classification des types de journée (S10-4).
 * Résultat du clustering k=4 sur production solaire journalière.
 * Précondition : simulation `completed`, sinon 409.
 */
export function getDayTypeDistribution(
  id: string,
  scenario: string = "central",
): Promise<DayTypeDistribution> {
  // `central` = défaut backend → on n'envoie le param que pour n1/n2 (requête du
  // chemin classique strictement identique).
  const params: Record<string, string> =
    scenario && scenario !== "central" ? { scenario } : {};
  return apiGet<DayTypeDistribution>(`${BASE}/${id}/day-type-distribution`, {
    params,
  });
}

/**
 * Récupère le profil 48 pts (maille 30 min) d'une journée arbitraire de la
 * période simulée (S17-3, contrat figé S17-1). `date` au format ISO
 * `YYYY-MM-DD`. Vue d'une journée existante, pas un nouveau calcul KPI.
 *
 * Erreurs propagées en `ApiError` : `404 SIMULATION_NOT_FOUND`,
 * `409 SIMULATION_NOT_COMPLETED`, `404 DAY_PROFILE_DATE_OUT_OF_RANGE`
 * (hors `[period_start, period_end]`), `422` (date malformée).
 */
export function getDayProfile(
  id: string,
  date: string,
): Promise<DayProfileRead> {
  return apiGet<DayProfileRead>(`${BASE}/${id}/day-profile`, {
    params: { date },
  });
}

/**
 * Empreinte conso **courante** du site sur la période de la simulation (S18-3,
 * ADR-004 D6a). Recalcul live (lecture + hash canonique S18-1), **sans run** :
 * sert uniquement l'avertissement « variante non comparable » du wizard variante
 * (S18-6). Aucun query param (la période est dérivée de la simu côté backend).
 *
 * Réponse : `{ fingerprint: string | null }`. `null` ⇒ le site n'a plus aucune
 * conso sur la période (pas le hash d'une chaîne vide). Owner-scopé : 404
 * `SIMULATION_NOT_FOUND` (inexistante ou cross-owner, anti-énumération).
 */
export function getCurrentConsumptionFingerprint(
  id: string,
): Promise<ConsumptionFingerprintRead> {
  return apiGet<ConsumptionFingerprintRead>(
    `${BASE}/${id}/consumption-fingerprint`,
  );
}
